import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getCourse } from "../../api/courseApi";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ClockIcon, PlayIcon, PdfIcon } from "../../components/ui/icons";
import { Clock4Icon, MedalIcon, X } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../components/ui/accordion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import noCourseImage from "@/assets/nocourse_image.svg";

import { useNavigate } from "react-router-dom";
import { CURRENT_COURSE_NAME_KEY } from "../../utils/localStoragekeys";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  detectTopicLanguage,
  getAvailableLanguages,
  readCourseLanguage,
  stripLanguageSuffix,
  writeCourseLanguage,
} from "@/utils/courseLanguage";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";
import { eligibleForAssessment } from "@/api/materialsApi";

// A material's progress row is scoped to the signed-in user by the API, so at
// most one entry comes back. `isCompleted` latches true at >=95% watched and is
// never cleared, so a rewatch keeps reading as Completed — which is why the
// action label says "Rewatch" rather than implying unfinished work.
const getMaterialStatus = (material) => {
  const progress = material?.materialProgress?.[0];

  if (progress?.isCompleted) {
    return { label: "Completed", variant: "green", started: true };
  }

  const percent = Math.round(Number(progress?.completionPercentage ?? 0));
  if (percent > 0) {
    return { label: `In Progress · ${percent}%`, variant: "yellow", started: true };
  }

  return { label: "Not Started", variant: "gray", started: false };
};

// What the button should say, given the file type and how far the user got.
const getActionLabel = (material, status) => {
  const type = material?.fileUpload?.fileType?.toLowerCase();
  const verb = type === "video" ? "Watch" : "View";

  if (status.label === "Completed") {
    return type === "video" ? "Rewatch video" : `Review ${type}`;
  }
  if (status.started) {
    return type === "video" ? "Resume video" : `Resume ${type}`;
  }
  return `${verb} ${type}`;
};

export default function CourseDetails() {
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [isCourseComplete, setIsCourseComplete] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  function handleAssessmentAttempt() {
    setShowAssessmentDialog(false);
    navigate(
      `/dashboard/courses/${course.id}/test/${course.courseAssessment.id}`,
      {
        state: { courseName: course.title },
      }
    );
  }

  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  // Session-scoped so a Hindi speaker does not re-pick on every course, but
  // still resets in a fresh tab. Read on mount, which is also how a language
  // reported by the player gets picked up when the learner comes back here.
  const [language, setLanguage] = useState(readCourseLanguage);

  const availableLanguages = getAvailableLanguages(course?.topics);

  const handleLanguageChange = (value) => {
    setLanguage(value);
    writeCourseLanguage(value);
  };

  const loadCourse = () => {
    setLoading(true);
    setError(null);
    getCourse(courseId)
      .then((res) => {
        if (res.success && res.data) {
          const courseTitle = res.data.name;
          setCourse({
            ...res.data,
            title: courseTitle,
            tags: res.data.tags,
            minPassPercentage: res.data?.courseAssessment?.passingScore,
            duration: res.data.duration ?? 10,
            image: res.data.coverImage
              ? import.meta.env.VITE_CDN_LINK + res.data.coverImage
              : noCourseImage,
          });
          localStorage.setItem(CURRENT_COURSE_NAME_KEY, courseTitle);
        } else {
          setError(res.message || "Failed to fetch course details");
        }
      })
      .catch((err) =>
        setError(getApiErrorMessage(err, "Failed to fetch course details")),
      )
      .finally(() => setLoading(false));
  };

  const handleAssessmentClick = async () => {
    try {
      setCheckingEligibility(true);
      const response = await eligibleForAssessment(courseId);
      if (response.success && response.data) {
        setIsCourseComplete(response.data.eligible);
      } else {
        setIsCourseComplete(false);
      }
      setShowAssessmentDialog(true);
    } catch (error) {
      console.error("Error checking assessment eligibility:", error);
      setIsCourseComplete(false);
      setShowAssessmentDialog(true);
    } finally {
      setCheckingEligibility(false);
    }
  };

  useEffect(() => {
    loadCourse();
  }, [courseId, retryCount]);

  return (
    <div className="w-full h-full p-2">
      {showAssessmentDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-6 relative border border-yellow-400">
            <button
              onClick={() => setShowAssessmentDialog(false)}
              className="absolute top-4 right-4 text-yellow-700 hover:text-yellow-900"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold w-full text-center text-[#5D93A5]">
                {isCourseComplete ? "Start Assessment" : "Course Not Fully Viewed"}
              </h2>
            </div>

            {isCourseComplete ? (
              <p className="text-black text-center text-base mb-6 font-medium">
                You&apos;ve completed the required course content in your chosen
                language. Are you ready to start the assessment?
              </p>
            ) : (
              <>
                <p className="text-black text-center text-base mb-4 font-medium">
                  You haven&apos;t fully viewed the course yet. We recommend
                  finishing the videos and materials in your chosen language
                  (English or Hindi) before attempting the assessment.
                </p>
                <p className="text-black text-center text-sm mb-6 text-gray-600">
                  You can still start the assessment if you&apos;re ready.
                </p>
              </>
            )}

            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                className="px-12 py-2 rounded-full font-medium bg-white"
                onClick={() => setShowAssessmentDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                className="px-6 py-2 rounded-full font-medium bg-[#5D93A5] text-white"
                onClick={() => handleAssessmentAttempt()}
              >
                Start Assessment
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full h-full pb-8 pr-4 p-6 font-sans">
        {loading ? (
          <div className="h-[350px] bg-[#e3eaf3] rounded-[18px] animate-pulse" />
        ) : error ? (
          <div className="text-[#d9534f] text-center mt-10">
            <p>{error}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setRetryCount((count) => count + 1)}
            >
              Try again
            </Button>
          </div>
        ) : !course ? (
          <div className="text-[#7a869a] text-center mt-10">
            Course not found.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header image + course tags */}
            <div className="flex flex-col md:flex-row gap-8 p-6 rounded-3xl bg-white shadow-sm">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="w-full max-h-[40vh] min-h-[20vh] min-w-[15vw] max-w-[25vw] flex-shrink-0 rounded-2xl overflow-hidden flex items-center justify-center">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="object-cover w-full h-full"
                  />
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div className="w-full space-y-3 mb-2">
                    <div className="flex text-nowrap overflow-x-auto gap-1 mb-4 w-3/4">
                      {(course.tags || []).map((tag, i) => (
                        <Badge
                          key={tag + "-" + i}
                          variant={"blue"}
                          className="rounded-xl px-3 py-1 text-xs font-semibold tracking-wide bg-[#D9ECF8] text-[#2B6A7F] shadow-none"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="font-bold text-2xl leading-tight font-helvetica">
                      {course.title}
                    </div>
                    <div className="text-[var(--default-font)] text-base font-light leading-normal line-clamp-6">
                      {course.description || "Course Description"}
                    </div>
                  </div>
                  {course?.courseAssessment?.id && (
                    <Button
                      className="rounded-full bg-[#5D93A5] text-white text-sm font-medium px-12 py-3"
                      disabled={checkingEligibility}
                      onClick={handleAssessmentClick}
                    >
                      {checkingEligibility ? "Checking..." : "Assessment"}
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-6 ml-auto mt-6">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-[var(--default-font)] text-sm">
                      Completion Time
                    </span>
                    <Badge
                      variant={"blue"}
                      className="gap-1 px-6 py-1.5 text-[var(--secondary-font)] rounded-full font-bold text-sm"
                    >
                      <Clock4Icon className="!w-4 !h-4 inline-block align-middle shrink-0 text-[var(--secondary-font)]" />
                      {`${course.courseDuration} mins`}
                    </Badge>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-[var(--default-font)] text-sm">
                      Min. Pass %age
                    </span>
                    <Badge
                      variant={"blue"}
                      className="gap-1 px-6 py-1.5 text-[var(--secondary-font)] rounded-full font-bold text-sm"
                    >
                      <MedalIcon className="!w-4 !h-4 inline-block align-middle shrink-0 text-[var(--secondary-font)]" />
                      {course.minPassPercentage}%
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
            {/* Course Overview Cards */}
            <div className="rounded-2xl pt-3">
              <div className="flex items-center justify-between gap-3 mb-4 md:mb-6">
                <div className="font-extrabold pl-3 text-xl text-[#2B6A7F]">
                  Course Overview
                </div>
                {/* Only rendered when the course actually ships more than one
                    language — on a single-language course it would be a
                    control that does nothing. */}
                {availableLanguages.length > 1 && (
                  <Select value={language} onValueChange={handleLanguageChange}>
                    <SelectTrigger className="w-32" aria-label="Content language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLanguages.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="rounded-3xl bg-white font-sans">
                {Array.isArray(course.topics) && course.topics.length > 0 ? (
                  (() => {
                    // Sort topics by displayOrder
                    const sortedTopics = course.topics
                      .filter((topic) => topic.isActive !== false)
                      // An unmarked topic has no language to disagree with, so
                      // it always shows: a course that never adopted the
                      // "(English)" / "(Hindi)" convention behaves exactly as
                      // it did before, and this can never hide everything.
                      .filter((topic) => {
                        const topicLanguage = detectTopicLanguage(topic);
                        return (
                          topicLanguage === null || topicLanguage === language
                        );
                      })
                      .sort(
                        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
                      );

                    // Find first topic with pending materials for default expansion
                    const firstTopicWithPending = sortedTopics.find(
                      (topic) =>
                        Array.isArray(topic.learningMaterials) &&
                        topic.learningMaterials.length > 0
                    );

                    const defaultExpandedValue = firstTopicWithPending
                      ? `topic-${firstTopicWithPending.id}`
                      : undefined;

                    return (
                      <Accordion
                        type="single"
                        collapsible
                        // defaultValue is uncontrolled: without a remount the
                        // open item would survive the filter change and could
                        // be a topic that is no longer listed.
                        key={language}
                        defaultValue={defaultExpandedValue}
                      >
                        {sortedTopics.map((topic, idx) => {
                          // Sort materials by displayOrder
                          const sortedMaterials = Array.isArray(
                            topic.learningMaterials
                          )
                            ? topic.learningMaterials.sort(
                                (a, b) =>
                                  (a.displayOrder || 0) - (b.displayOrder || 0)
                              )
                            : [];
                          // Counted rather than `every()`: an empty topic used to
                          // report Completed, because [].every() is true. Also
                          // guards materialProgress being absent.
                          const completedCount = sortedMaterials.filter(
                            (material) =>
                              getMaterialStatus(material).label === "Completed"
                          ).length;
                          const topicStatus =
                            sortedMaterials.length === 0
                              ? { label: "No Materials", variant: "gray" }
                              : completedCount === sortedMaterials.length
                                ? { label: "Completed", variant: "green" }
                                : completedCount > 0
                                  ? {
                                      label: `In Progress · ${completedCount}/${sortedMaterials.length}`,
                                      variant: "yellow",
                                    }
                                  : { label: "Not Started", variant: "gray" };

                          return (
                            <AccordionItem
                              key={topic.id}
                              value={`topic-${topic.id}`}
                              className="border-none"
                            >
                              <Card className="shadow-md transition-shadow gap-0 space-y-0">
                                <AccordionTrigger className="px-8 hover:no-underline">
                                  <CardHeader className="p-0 flex-1">
                                    <div className="flex items-start justify-between w-full">
                                      <div className="text-left">
                                        <CardTitle className="text-lg font-semibold text-[var(--default-font)] mb-2">
                                          {idx + 1}.{" "}
                                          {stripLanguageSuffix(
                                            topic.name || topic.title
                                          )}
                                        </CardTitle>
                                        {topic.description && (
                                          <p className="text-sm text-[var(--default-font)]/75 font-normal leading-relaxed">
                                            {topic.description}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 ml-4">
                                        <Badge
                                          variant={topicStatus.variant}
                                          className="text-xs"
                                        >
                                          {topicStatus.label}
                                        </Badge>
                                        <Badge
                                          variant="blue"
                                          className="text-xs"
                                        >
                                          {sortedMaterials.length} Material
                                          {sortedMaterials.length !== 1
                                            ? "s"
                                            : ""}
                                        </Badge>
                                      </div>
                                    </div>
                                  </CardHeader>
                                </AccordionTrigger>

                                <AccordionContent className="px-6">
                                  <CardContent className="px-4">
                                    {sortedMaterials.length > 0 ? (
                                      <table className="w-full border-collapse">
                                        <tbody>
                                          {sortedMaterials.map(
                                            (material, midx) => {
                                            const status =
                                              getMaterialStatus(material);
                                            return (
                                              <tr
                                                key={midx}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                              >
                                                {/* Number */}
                                                <td className="py-3 text-sm font-semibold pr-4">
                                                  {idx + 1}.{midx + 1}
                                                </td>

                                                {/* Title */}
                                                <td className="py-3 text-sm font-semibold w-1/4 text-wrap">
                                                  {material.title ||
                                                    `${topic.name} ${midx + 1}`}
                                                </td>

                                                {/* Type */}
                                                <td className="py-3 text-sm text-[#7a869a] gap-1 w-1/4 text-wrap">
                                                  <div className="flex items-center justify-center gap-1">
                                                    {material.fileUpload
                                                      ?.fileType || "-"}
                                                  </div>
                                                </td>

                                                {/* Duration */}
                                                <td className="py-3 text-sm text-[#7a869a] gap-1 w-1/4 text-wrap">
                                                  <div className="flex items-center gap-1 border border-[#5D93A5] rounded-full px-4 py-1 w-fit bg-[#F3F3F3]">
                                                    <ClockIcon className="w-4 h-4 text-[#5D93A5]" />
                                                    {material.fileUpload
                                                      ?.duration
                                                      ? Math.ceil(
                                                          material.fileUpload
                                                            .duration / 60
                                                        ) + " mins"
                                                      : "-"}
                                                  </div>
                                                </td>

                                                {/* Quiz */}
                                                <td className="py-3 text-sm text-[#5D93A5] w-1/4 text-wrap text-center">
                                                  {material.test
                                                    ? "Quiz at end of Chapter"
                                                    : "No Quiz associated"}
                                                </td>

                                                {/* Watched / completion status */}
                                                <td className="py-3 text-sm w-1/4 text-center">
                                                  <Badge
                                                    variant={status.variant}
                                                    className="text-xs"
                                                  >
                                                    {status.label}
                                                  </Badge>
                                                </td>

                                                {/* Action Button */}
                                                <td className="py-3 w-1/4 text-wrap">
                                                  {material.fileUpload
                                                    ?.fileType && (
                                                    <Button
                                                      variant="outline"
                                                      size="sm"
                                                      className="bg-[var(--primary-font)] border-[var(--primary-font)] text-white hover:bg-[var(--secondary-font)]"
                                                      onClick={() => {
                                                        localStorage.setItem(
                                                          CURRENT_COURSE_NAME_KEY,
                                                          course.title
                                                        );
                                                        navigate(
                                                          `/dashboard/courses/${course.id}/${material.id}`,
                                                          {
                                                            state: {
                                                              courseName:
                                                                course.title,
                                                              topicName:
                                                                material.title,
                                                            },
                                                          }
                                                        );
                                                      }}
                                                    >
                                                      {getActionLabel(
                                                        material,
                                                        status
                                                      )}
                                                    </Button>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                            }
                                          )}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <div className="text-[#7a869a] text-center py-8">
                                        No materials found for this topic.
                                      </div>
                                    )}
                                  </CardContent>
                                </AccordionContent>
                              </Card>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    );
                  })()
                ) : (
                  <div className="text-[#7a869a] text-center py-8">
                    No topics found.
                  </div>
                )}
              </div>
            </div>
            <div className="w-full h-[50px]" />
          </div>
        )}
      </div>
    </div>
  );
}